// BLTZ-731 (epic BLTZ-728): the public status page at bltz.ai/status.
//
// Read when something is broken, by people who may not be able to sign in.
// So: no site nav, no marketing chrome, no dependency on anything but the two
// status endpoints, and never a reassuring default when we cannot answer.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import shieldLogo from '../../assets/shield_logo.png';
import './StatusPage.css';

const API = process.env.REACT_APP_API_URL || 'https://bltzbackend.vercel.app';

const STATUS_LABEL = {
  investigating: 'Investigating',
  update: 'Update',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

// The banner states a CONDITION, not an incident title. An incident called
// "Test" or "Delayed detection processing" is not a service state, and the
// banner is the one line most people read before deciding whether to act.
const CONDITION = {
  minor: 'Minor Service Disruption',
  major: 'Partial System Outage',
  critical: 'Major System Outage',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const p2 = (n) => String(n).padStart(2, '0');

// Everything on this page is UTC and says so. Localising would mean two people
// comparing notes during an incident reading different times off the same page.
const ymdUTC = (d) => `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`;
const dayLabel = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};
const stamp = (iso) => {
  const d = new Date(iso);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())} UTC`;
};

// Every date from window_end back to window_start. The API returns the window
// precisely so this is possible: a day with no incidents has no rows, by
// design, so the days themselves have to come from somewhere.
function daysInWindow(startYmd, endYmd) {
  const out = [];
  const cur = new Date(`${endYmd}T00:00:00Z`);
  const stop = new Date(`${startYmd}T00:00:00Z`);
  while (cur >= stop) {
    out.push(ymdUTC(cur));
    cur.setUTCDate(cur.getUTCDate() - 1);
  }
  return out;
}

export default function StatusPage() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  // Distinct from "nothing is open". Unreachable must never look like healthy.
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        fetch(`${API}/api/status/summary`).then((r) => r.json()),
        fetch(`${API}/api/status/incidents?page=${p}`).then((r) => r.json()),
      ]);
      // The endpoints answer 503 with success:false rather than a default, so a
      // failure is explicit rather than something to be inferred.
      if (!s || s.success !== true || !h || h.success !== true) {
        setFailed(true); setSummary(null); setHistory(h && h.success ? h : null);
      } else {
        setFailed(false); setSummary(s); setHistory(h);
      }
    } catch (e) {
      // A non-JSON body, a gateway error, a network failure. All of it means
      // the same thing here: we do not know, and we must not imply we do.
      setFailed(true); setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page); }, [load, page]);
  useEffect(() => { document.title = 'Bltz Status'; }, []);

  const worst = summary && summary.worst_severity;
  const maint = (summary && summary.open_maintenance) || [];
  // A fault outranks planned work. Maintenance only takes the banner when
  // nothing is actually wrong, and even then it is grey, not green and not
  // amber: nothing is broken, but something is going on.
  const maintOnly = summary && summary.operational && maint.length > 0;
  const bannerClass = failed || !summary
    ? 'unknown'
    : maintOnly ? 'maintenance' : summary.operational ? 'ok' : (worst || 'major');
  const openCount = summary ? summary.open_incidents.length : 0;
  const bannerText = failed || !summary
    ? 'Status unavailable'
    : maintOnly
      ? 'Scheduled Maintenance'
      : summary.operational
        ? 'All Systems Operational'
        : CONDITION[worst] || 'Service Disruption';

  const days = history ? daysInWindow(history.window_start, history.window_end) : [];
  const today = ymdUTC(new Date());

  // The RPC returns incidents by OVERLAP, not by start date, so a long-running
  // one can have begun before this window. Keying it on started_at put it in a
  // bucket no day row reads, and it silently vanished: a still-open critical
  // incident could disappear under a red banner reading Major System Outage.
  // That is the exact failure the overlap predicate was added to the RPC to
  // prevent, re-created one layer up.
  //
  // It ran during the window, so it belongs on the window's first day.
  const byDay = {};
  if (history) {
    for (const inc of history.incidents) {
      const started = ymdUTC(new Date(inc.started_at));
      const k = started < history.window_start ? history.window_start : started;
      (byDay[k] = byDay[k] || []).push(inc);
    }
  }

  // A modal rather than an inline field, because there are now three required
  // fields and a captcha widget, which is more than a page header can carry.
  const [subOpen, setSubOpen] = useState(false);
  const [sub, setSub] = useState({ name: '', company: '', email: '' });
  const [subState, setSubState] = useState('idle'); // idle | sending | done
  const [subError, setSubError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  // Rendered when the modal opens, not on mount: the container does not exist
  // until then, and grecaptcha refuses to render into nothing. Same site key
  // and enterprise script the contact and demo forms already use; the action
  // differs so a token minted on one form cannot be replayed against another.
  useEffect(() => {
    if (!subOpen) return undefined;
    let interval;
    const render = () => {
      const g = window.grecaptcha && window.grecaptcha.enterprise;
      if (g && captchaRef.current && !captchaRef.current.hasChildNodes()) {
        g.render(captchaRef.current, {
          sitekey: '6Lcfz4ssAAAAAIhyh2nC-YLe_YbDc6nXf9EiG9HQ',
          action: 'status_subscribe',
          callback: (t) => { setCaptchaToken(t); setSubError(''); },
          'expired-callback': () => setCaptchaToken(null),
        });
      }
    };
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      window.grecaptcha.enterprise.ready(render);
    } else {
      // The script is async in index.html, so on a cold load it may not be
      // there yet. Poll rather than fail: the alternative is a form nobody can
      // submit and no explanation why.
      interval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval);
          window.grecaptcha.enterprise.ready(render);
        }
      }, 200);
    }
    return () => interval && clearInterval(interval);
  }, [subOpen]);

  const closeSub = () => {
    setSubOpen(false);
    setSubError('');
    setCaptchaToken(null);
    if (subState !== 'done') setSubState('idle');
  };

  const submitSubscribe = async (e) => {
    e.preventDefault();
    if (subState === 'sending') return;
    if (!captchaToken) {
      setSubError('Please complete the verification below.');
      return;
    }
    setSubState('sending');
    setSubError('');
    try {
      const r = await fetch(`${API}/api/status/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...sub, recaptchaToken: captchaToken }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.success !== true) {
        // The endpoint is deliberately vague about anything to do with the
        // address, so whatever comes back is a field problem, a verification
        // one, or a write that did not happen. All three are worth showing and
        // none of them reveals whether the address was already on the list.
        setSubError(body.error || 'That did not work. Please try again.');
        setSubState('idle');
        setCaptchaToken(null);
        if (window.grecaptcha && window.grecaptcha.enterprise) window.grecaptcha.enterprise.reset();
        return;
      }
      setSubState('done');
    } catch {
      setSubError('Could not reach us just now. Please try again.');
      setSubState('idle');
    }
  };

  return (
    <div className="status-page">
      <div className="status-inner">
        <header className="status-header">
          <div className="status-brand">
            <img src={shieldLogo} alt="Bltz" />
            <span>Bltz Status</span>
          </div>
          <button className="status-subscribe" type="button" onClick={() => setSubOpen(true)}>
            {subState === 'done' ? 'Subscribed' : 'Subscribe to Updates'}
          </button>
        </header>

        <div className={`status-banner ${bannerClass}`}>
          {bannerText}
          {(failed || !summary) ? (
            <div className="status-banner-note">
              This page could not reach the status service, so it cannot tell you
              whether anything is wrong. It is not saying everything is fine.
            </div>
          ) : !summary.operational ? (
            <div className="status-banner-note">
              {openCount === 1
                ? summary.open_incidents[0].title
                : `${openCount} incidents open. See below.`}
            </div>
          ) : maintOnly && (
            <div className="status-banner-note">
              {maint.length === 1 ? maint[0].title : `${maint.length} maintenance notices. See below.`}
            </div>
          )}
        </div>

        <h1 className="status-history-title">Past Incidents</h1>

        {loading && !history && <p className="status-loading">Loading…</p>}

        {days.map((ymd) => (
          <section className="status-day" key={ymd}>
            <div className="status-day-date">{dayLabel(ymd)}</div>
            {!byDay[ymd] ? (
              <p className="status-day-empty">
                {ymd === today ? 'No incidents reported today.' : 'No incidents reported.'}
              </p>
            ) : (
              byDay[ymd].map((inc) => (
                <article className="status-incident" key={inc.id}>
                  <h2 className={`status-incident-title ${inc.type === 'maintenance' ? 'maintenance' : inc.severity}`}>
                    {inc.title}
                    {/* Severity in words as well as colour. The title tint alone
                        carries it for people who can see the difference; the badge
                        carries it for everyone else. Maintenance has no severity
                        to state, so it says what it is instead. */}
                    <span className={`status-sev ${inc.type === 'maintenance' ? 'maintenance' : inc.severity}`}>
                      {inc.type === 'maintenance' ? 'maintenance' : inc.severity}
                    </span>
                  </h2>

                  {/* The incident's own text. It was fetched and thrown away, so a
                      freshly raised incident rendered as a bare title with nothing
                      under it: no wording, no time, nothing to act on. */}
                  {/* Clamping puts it on the window's first day, which would
                      otherwise read as though it started there. Say when it
                      actually began. */}
                  {ymdUTC(new Date(inc.started_at)) < history.window_start && (
                    <p className="status-incident-since">
                      Ongoing since {dayLabel(ymdUTC(new Date(inc.started_at)))}
                    </p>
                  )}

                  {inc.description && (
                    <p className="status-incident-desc">{inc.description}</p>
                  )}

                  {/* Until the first update is posted there is no timestamp on the
                      page at all. When an incident began is the minimum a reader
                      needs, so state it. */}
                  {inc.updates.length === 0 && (
                    <div className="status-update-time">Reported {stamp(inc.started_at)}</div>
                  )}

                  {inc.updates.map((u) => (
                    <div className="status-update" key={u.id}>
                      <p className="status-update-body">
                        <span className="status-update-status">
                          {STATUS_LABEL[u.status] || u.status}
                        </span>
                        {' - '}
                        {u.body}
                      </p>
                      <div className="status-update-time">{stamp(u.created_at)}</div>
                    </div>
                  ))}
                </article>
              ))
            )}
          </section>
        ))}

        <footer className="status-footer">
          <button
            className="status-pager"
            type="button"
            onClick={() => setPage((n) => n + 1)}
            disabled={!history || !history.has_older || loading}
          >
            ← Incident History
          </button>
          {page > 1 && (
            <button className="status-pager" type="button" onClick={() => setPage((n) => Math.max(1, n - 1))} disabled={loading}>
              Back to current →
            </button>
          )}
        </footer>
        {subOpen && (
          <div
            className="status-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Subscribe to status updates"
            onClick={closeSub}
          >
            <div className="status-modal" onClick={(ev) => ev.stopPropagation()}>
              {subState === 'done' ? (
                <>
                  <h2>You are subscribed</h2>
                  <p className="status-modal-note">
                    We will email you when there is an incident or planned maintenance.
                  </p>
                  <div className="status-modal-actions">
                    <button className="status-subscribe" type="button" onClick={closeSub}>Close</button>
                  </div>
                </>
              ) : (
                <form onSubmit={submitSubscribe}>
                  <h2>Subscribe to status updates</h2>
                  <p className="status-modal-note">
                    Get an email when there is an incident or planned maintenance.
                  </p>

                  <label htmlFor="sub-name">Name</label>
                  <input id="sub-name" type="text" required autoFocus maxLength={120}
                         value={sub.name}
                         onChange={(ev) => setSub({ ...sub, name: ev.target.value })} />

                  <label htmlFor="sub-company">Company</label>
                  <input id="sub-company" type="text" required maxLength={200}
                         value={sub.company}
                         onChange={(ev) => setSub({ ...sub, company: ev.target.value })} />

                  <label htmlFor="sub-email">Email</label>
                  <input id="sub-email" type="email" required maxLength={320}
                         value={sub.email}
                         onChange={(ev) => setSub({ ...sub, email: ev.target.value })} />

                  <div className="status-modal-captcha" ref={captchaRef} />

                  {subError && <p className="status-modal-error" role="alert">{subError}</p>}

                  <div className="status-modal-actions">
                    <button type="button" className="status-modal-cancel" onClick={closeSub}>Cancel</button>
                    <button className="status-subscribe" type="submit" disabled={subState === 'sending'}>
                      {subState === 'sending' ? 'Subscribing…' : 'Subscribe'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
